import { Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { PoAddress, PoCustomer } from '@/lib/po-draft-import-api';
import { addressIsBlank, lineQty, lineSubtotal, money, type DraftLine } from '@/lib/po-draft-utils';

interface Props {
  open: boolean; busy: boolean; onCancel: () => void; onConfirm: () => void;
  customer: PoCustomer | null; reference: string; lines: DraftLine[]; currency: string; address: PoAddress;
}

export function ConfirmDialog({ open, busy, onCancel, onConfirm, customer, reference, lines, currency, address }: Props) {
  const units = lines.reduce((s, l) => s + lineQty(l), 0);
  const ship = addressIsBlank(address) ? 'No shipping address (pickup)' :
    [`${address.firstName} ${address.lastName}`.trim(), address.company, address.address1, address.address2, `${address.city} ${address.province} ${address.zip}`.trim(), address.countryCode].filter(Boolean).join(', ');
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <AlertDialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="dialog-confirm-create">
        <AlertDialogHeader>
          <AlertDialogTitle>Create Shopify draft order?</AlertDialogTitle>
          <AlertDialogDescription>This creates a draft only. No invoice is emailed and the order is not completed.</AlertDialogDescription>
        </AlertDialogHeader>
        <dl className="text-[13px] grid grid-cols-[110px_1fr] gap-y-1.5">
          <dt className="text-muted-foreground">Customer</dt><dd data-testid="text-confirm-customer">{customer?.name || customer?.email}{customer?.company ? ` (${customer.company})` : ''}</dd>
          <dt className="text-muted-foreground">Reference</dt><dd className="font-mono" data-testid="text-confirm-reference">{reference}</dd>
          <dt className="text-muted-foreground">Lines</dt><dd>{lines.length} lines, {units} units</dd>
          <dt className="text-muted-foreground">Subtotal</dt><dd data-testid="text-confirm-subtotal">{money(lineSubtotal(lines), currency)} <span className="text-xs text-muted-foreground">at current Shopify prices, excl. tax and shipping Shopify calculates</span></dd>
          <dt className="text-muted-foreground">Ship to</dt><dd>{ship}</dd>
          {!addressIsBlank(address) && <><dt className="text-muted-foreground">Shipping rate</dt><dd>Cheapest available Shopify rate, applied automatically when creating the draft.</dd></>}
          <dt className="text-muted-foreground">Shopify note</dt><dd className="font-mono">{reference}</dd>
        </dl>
        <ul className="max-h-40 overflow-y-auto border border-border rounded divide-y divide-border text-xs">
          {lines.map((l, i) => (
            <li key={l.key} className="px-2 py-1.5 flex gap-2"><span className="flex-1 truncate">{l.variant?.productTitle} {l.variant?.title !== 'Default Title' ? l.variant?.title : ''}</span>
              <span className="font-mono">{lineQty(l)} x {money(Number(l.variant?.price), currency)}</span><span className="sr-only">{i}</span></li>
          ))}
        </ul>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} onClick={onCancel} data-testid="button-cancel-create">Back to review</AlertDialogCancel>
          <Button disabled={busy} onClick={onConfirm} data-testid="button-confirm-create">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{busy ? 'Creating…' : 'Create draft order'}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
